import axios from 'axios';

import { version } from '@/../package.json';
import { MATOMO_CONFIG } from '@/constants';
import { hasAnalyticalConsent } from '@/helper/cookie-consent/application';
import { getConsentCookie } from '@/helper/cookie-consent/storage';
import { router } from '@/router';
import { useAuthStore } from '@/stores/auth';
import { getPlatformName, getVisitorId } from '../visitor-id';

/**
 * Sends a tracking request to Matomo via the HTTP API.
 * Automatically handles consent validation and fallback to backend tracking.
 * @param params - Additional tracking parameters.
 * @returns Promise resolving to `true` if tracking was successful, otherwise `false`.
 */
export async function trackViaHTTPApi(params: string[]): Promise<boolean> {
  const urlPath = router.currentRoute.value.fullPath;

  // Check if consent status is cached, if not check directly from localStorage
  if (window._matomoOkay === undefined) {
    // Consent check hasn't been initialized yet, check directly
    const consentCookie = getConsentCookie();
    const hasConsent = hasAnalyticalConsent(consentCookie);
    window._matomoOkay = hasConsent;

    if (!hasConsent) {
      console.log(
        '[Matomo helper] Consent checked directly from localStorage, no analytical consent, skipping tracking for ' +
          urlPath,
        params,
      );
      return false;
    }

    console.log(
      '[Matomo helper] Consent checked directly from localStorage, analytical consent found',
    );
  }

  if (!window._matomoOkay) {
    // Consent was checked and denied
    console.log(
      '[Matomo helper] User has not consented, skipping tracking for ' +
        urlPath,
      params,
    );
    return false;
  }

  const platform = getPlatformName();
  const appVersion: string = version;
  const authStore = useAuthStore();
  const loggedIn: string = (authStore.user !== null ? 1 : 0).toString();
  const API_BASE_URL =
    import.meta.env.MODE === 'production'
      ? 'https://app-api.uk-koeln.de/api'
      : 'https://api.uk-aktuell.sunzity.de/api';
  const APP_DOMAIN =
    import.meta.env.MODE === 'production'
      ? 'https://app.uk-koeln.de'
      : 'https://app.uk-aktuell.sunzity.de';

  const defaultParams = [
    `idsite=${MATOMO_CONFIG.SITE_ID}`, // matomo site id
    'rec=1', // must be set, required by HTTP API
    `url=${encodeURIComponent(APP_DOMAIN + urlPath)}`, // current URL
    `dimension1=${loggedIn}`, // custom dimension 1: login status
    `dimension2=${appVersion}`, // custom dimension 2: app version
    `dimension3=${platform}`, // custom dimension 3: platform type (PWA, Android or iOS)
    `ua=${encodeURIComponent(navigator.userAgent)}`, // user agent
    `res=${window.screen.width + 'x' + window.screen.height}`, // device resolution
    `_id=${getVisitorId()}`, // The unique visitor ID, must be a 16 characters hexadecimal string.
    `rand=${Math.random().toString(36).substring(2, 8)}`, // cache buster
  ];

  params = [...defaultParams, ...params];

  const matomoUrl = `${MATOMO_CONFIG.TRACKER_URL}?${params.join('&')}`;
  console.log('[Matomo helper] tracking via HTTP API ' + matomoUrl, params);

  try {
    const postResponse = await axios.post(matomoUrl, null, {
      showLoading: false,
    });
    if (postResponse.status === 200) {
      console.log('[Matomo helper] tracking via HTTP API success');
      return true;
    }
  } catch (err) {
    console.warn('[Matomo helper] tracking via HTTP API failed', err);
  }

  try {
    const getResponse = await axios.get(
      `${API_BASE_URL}/matomo?hl=de&${params.join('&')}`,
      { showLoading: false },
    );
    if (getResponse.status === 200) {
      console.log('[Matomo helper] tracking via Backend success');
      return true;
    }
  } catch (err) {
    console.warn('[Matomo helper] tracking via Backend failed', err);
  }

  console.log('[Matomo helper] all Matomo tracking attempts failed');
  return false;
}
